"use client"

import React, { useEffect } from "react"

// Margin in pixels to lazyload image before entering viewport
const LAZY_LOAD_MARGIN = 50

// It's intended to keep `src` the last attribute because React updates
// attributes in order. If we keep `src` the first one, Safari will
// immediately start to fetch `src`, before `sizes` and `srcSet` are even
// updated by React. That causes multiple unnecessary requests if `srcSet`
// and `sizes` are defined.
const intersectionCallback: IntersectionObserverCallback = (entries, observer) => {
  entries.forEach(function (element) {
    if (element.isIntersecting) {
      const currentElement = element.target as HTMLVideoElement | HTMLImageElement

      if (currentElement.tagName === "VIDEO") {
        ;(element.target as HTMLVideoElement).load()
      }

      if (currentElement.dataset.srcset) {
        ;(currentElement as HTMLImageElement).srcset = currentElement.dataset.srcset
        element.target.removeAttribute("data-srcset")
      }

      if (currentElement.dataset.src) {
        currentElement.src = currentElement.dataset.src
        element.target.removeAttribute("data-src")
      }

      // Fade in animation
      element.target.classList.add("lazy-loaded")
      element.target.classList.remove("lazy-hidden")

      observer.unobserve(element.target)
    }
  })
}

// Only use one intersectionOberserver instance
let lazyLoadObserver: IntersectionObserver
function getLazyLoadObserver() {
  if (typeof window === "undefined") return undefined

  if (!lazyLoadObserver) {
    lazyLoadObserver = new IntersectionObserver(intersectionCallback, { rootMargin: `0px 0px ${LAZY_LOAD_MARGIN}px 0px` })
  }

  return lazyLoadObserver
}

export function useLazyLoad(lazyLoadRef: React.RefObject<HTMLImageElement> | React.RefObject<HTMLVideoElement>, src: string) {
  useEffect(() => {
    if (!lazyLoadRef.current) return
    if (lazyLoadRef.current.src === src) return

    getLazyLoadObserver()?.observe(lazyLoadRef.current)
    const currentChildRef = lazyLoadRef.current

    return () => {
      getLazyLoadObserver()?.unobserve(currentChildRef)
    }
  }, [lazyLoadRef, src])
}
